import _extends from '@babel/runtime/helpers/extends';
import React from 'react';
import PropTypes from 'prop-types';
import { p as props, d as defaults } from './componentEl-fcbc301e.js';
import { a as addClassNames, c as classNamePropTypes } from './classNames-1ed461aa.js';
import { a as addSpacingClasses, m as marginPropTypes } from './spacing-0f39ba4a.js';

const presets = {
  header1: {
    componentEl: 'h1',
    color: 'ink',
    size: 'xxl',
    weight: 'reg',
    inline: false,
    uppercase: false,
    margin: {
      top: 'none',
      bottom: 'md'
    }
  },
  header2: {
    componentEl: 'h2',
    color: 'ink',
    size: 'xl',
    weight: 'reg',
    inline: false,
    uppercase: false,
    margin: {
      top: 'none',
      bottom: 'md'
    }
  },
  header3: {
    componentEl: 'h3',
    color: 'ink',
    size: 'lg',
    weight: 'reg',
    inline: false,
    uppercase: false,
    margin: {
      top: 'none',
      bottom: 'sm'
    }
  },
  header4: {
    componentEl: 'h4',
    color: 'ink',
    size: 'md',
    weight: 'bold',
    inline: false,
    uppercase: false,
    margin: {
      top: 'none',
      bottom: 'sm'
    }
  },
  link: {
    componentEl: 'a',
    color: 'blurple',
    size: 'inherit',
    weight: 'med',
    inline: true,
    uppercase: false,
    margin: null
  },
  label1: {
    componentEl: 'span',
    color: 'ink',
    size: 'sm',
    weight: 'med',
    inline: false,
    uppercase: false,
    margin: {
      top: 'none',
      bottom: 'sm'
    }
  },
  label2: {
    componentEl: 'span',
    color: 'asphalt',
    size: 'sm',
    weight: 'bold',
    inline: false,
    uppercase: true,
    margin: {
      top: 'none',
      bottom: 'sm'
    }
  },
  label3: {
    componentEl: 'h4',
    color: 'ink',
    size: 'sm',
    weight: 'bold',
    inline: false,
    uppercase: false,
    margin: {
      top: 'none',
      bottom: 'xs'
    }
  },
  note: {
    componentEl: 'small',
    color: 'asphalt',
    size: 'sm',
    weight: 'reg',
    inline: false,
    uppercase: false,
    margin: null
  },
  bodytext: {
    componentEl: 'p',
    color: 'ink',
    size: 'md',
    weight: 'reg',
    inline: false,
    uppercase: false,
    margin: {
      top: 'none',
      bottom: 'sm'
    }
  },
  code: {
    componentEl: 'code',
    color: 'ink',
    size: 'inherit',
    weight: 'reg',
    inline: true,
    uppercase: false,
    margin: null
  }
};
function Typography({
  componentEl,
  align,
  className,
  color,
  inline,
  margin,
  weight,
  size,
  truncate,
  uppercase,
  variant,
  whiteSpace,
  ...otherProps
}) {
  let cssClasses = ['st_text'];
  cssClasses = addClassNames(className, cssClasses);
  if (variant) {
    cssClasses.push(`st_text--v-${variant}`);
  }
  let componentProps = {};

  // Setting defaults here and not defaultProps
  // Updated when we added supprot for variants in order to allow overrides
  // Otherwise we have no way of knowing whether a user meant o override a variant
  // or whether we are getting the prop value from defaultProps
  const defaults$1 = {
    align: 'inherit',
    componentEl: defaults,
    color: 'ink',
    inline: false,
    weight: 'reg',
    size: 'md',
    uppercase: false,
    truncate: false
  };

  // set base font styles
  // either based on variant defaults
  // or just text defaults
  if (variant && Object.prototype.hasOwnProperty.call(presets, variant)) {
    componentProps = {
      ...defaults$1,
      ...presets[variant]
    };
  } else {
    componentProps = {
      ...defaults$1
    };
  }
  if (componentEl) componentProps.componentEl = componentEl;
  if (margin) componentProps.margin = margin;
  if (color) componentProps.color = color;
  if (typeof inline === 'boolean') componentProps.inline = inline;
  if (weight) componentProps.weight = weight;
  if (size) componentProps.size = size;
  if (typeof uppercase === 'boolean') componentProps.uppercase = uppercase;
  cssClasses = addSpacingClasses({
    margin: componentProps.margin,
    cssClasses
  });
  if (align) {
    cssClasses.push(`st_text--align-${align}`);
  }
  if (componentProps.inline) cssClasses.push('st_text--inline');
  if (componentProps.size) {
    cssClasses.push(`st_text--${componentProps.size}`);
  }
  if (truncate) cssClasses.push('st_text--truncate');
  if (componentProps.color) cssClasses.push(`st_text--color-${componentProps.color}`);
  if (componentProps.weight) cssClasses.push(`st_text--weight-${componentProps.weight}`);
  if (componentProps.uppercase) cssClasses.push('st_text--uppercase');
  if (whiteSpace) cssClasses.push(`st_whitespace--${whiteSpace}`);
  const Component = componentProps.componentEl;
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps));
}
Typography.propTypes = {
  /**
  dom element or component to use for outermost wrapper.
  Defaults to most appropriate tag for variant, or div if unspecified
  */
  componentEl: props,
  /** text alignment */
  align: PropTypes.oneOf(['inherit', 'left', 'center', 'right', 'justify']),
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /**
  Color of text.
  Defaults to most appropriate for variant,
  or ink if no variant.
  */
  color: PropTypes.oneOf(['white', 'ink', 'blurple', 'asphalt', 'lava', 'emerald', 'inherit']),
  /**
  Whether text is inline or block.
  Defaults to most appropriate for variant, or false if no variant.
  */
  inline: PropTypes.bool,
  /** margin for box, if any */
  margin: marginPropTypes,
  /**
  Size of text. Defaults to appropriate for variant, or md if no variant. */
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl', 'xxl', 'inherit']),
  /**
  Font weight. Defaults to appropriate weight for variant, or reg if no variant
  */
  weight: PropTypes.oneOf(['reg', 'med', 'bold', 'black']),
  /** whether text should truncate with an ellipsis if longer than one line */
  truncate: PropTypes.bool,
  /**
  Whether text should be all-caps or not.
  Defaults to most appropriate for variant, or false if no variant */
  uppercase: PropTypes.bool,
  /**
    Shortcut for setting multiple properties at once through presets.
  */
  variant: PropTypes.oneOf(['header1', 'header2', 'header3', 'header4', 'bodytext', 'link', 'label1', 'label2', 'label3', 'note', 'code']),
  whiteSpace: PropTypes.oneOf(['normal', 'nowrap', 'pre', 'pre-wrap', 'pre-line', 'break-spaces'])
};
Typography.defaultProps = {
  align: 'inherit',
  truncate: false,
  whiteSpace: 'normal'
};

export { Typography as T };
